import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserProgress } from '../hooks/useUserProgress';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { LevelUpOverlay } from '../components/ui/LevelUpOverlay';
import { StreakCelebrationOverlay } from '../components/ui/StreakCelebrationOverlay';
import { SilentErrorBoundary } from '../components/ui/SilentErrorBoundary';

interface Achievement {
    title: string;
    description?: string;
    earned_at?: string;
}

// Level Tier System
type LevelTier = 'Novice' | 'Apprentice' | 'Scholar' | 'Sage' | 'Legend';

interface LevelInfo {
    level: number;
    tier: LevelTier;
    xpForCurrentLevel: number;
    xpToNextLevel: number;
    progressPercent: number;
}

/**
 * Calculate level info based on total XP using tiered progression:
 * - Novice (Lv 1-10): 500 XP per level
 * - Apprentice (Lv 11-20): 1,000 XP per level
 * - Scholar (Lv 21-40): 2,500 XP per level
 * - Sage (Lv 41-60): 5,000 XP per level
 * - Legend (Lv 61+): 10,000 XP per level
 */
export function calculateLevelInfo(totalXp: number): LevelInfo {
    let level = 1;
    let xpRemaining = totalXp;

    // Tier thresholds (cumulative XP needed to reach the start of each tier)
    const tiers = [
        { maxLevel: 10, xpPerLevel: 500 },
        { maxLevel: 20, xpPerLevel: 1000 },
        { maxLevel: 40, xpPerLevel: 2500 },
        { maxLevel: 60, xpPerLevel: 5000 },
        { maxLevel: Infinity, xpPerLevel: 10000 },
    ];

    for (const tier of tiers) {
        while (level <= tier.maxLevel && xpRemaining >= tier.xpPerLevel) {
            xpRemaining -= tier.xpPerLevel;
            level++;
        }
        if (level <= tier.maxLevel) break;
    }

    // Determine which tier the current level falls into
    let tier: LevelTier;
    let xpForCurrentLevel: number;

    if (level <= 10) {
        tier = 'Novice';
        xpForCurrentLevel = 500;
    } else if (level <= 20) {
        tier = 'Apprentice';
        xpForCurrentLevel = 1000;
    } else if (level <= 40) {
        tier = 'Scholar';
        xpForCurrentLevel = 2500;
    } else if (level <= 60) {
        tier = 'Sage';
        xpForCurrentLevel = 5000;
    } else {
        tier = 'Legend';
        xpForCurrentLevel = 10000;
    }

    const progressPercent = (xpRemaining / xpForCurrentLevel) * 100;

    return {
        level,
        tier,
        xpForCurrentLevel,
        xpToNextLevel: xpForCurrentLevel - xpRemaining,
        progressPercent,
    };
}

/**
 * Tier name for a given level. Mirrors the band logic in calculateLevelInfo so
 * the tier stays correct when we drive display off the server's persisted level
 * (the canonical, achievement-relevant value) rather than recomputing from XP.
 */
export function tierForLevel(level: number): LevelTier {
    if (level <= 10) return 'Novice';
    if (level <= 20) return 'Apprentice';
    if (level <= 40) return 'Scholar';
    if (level <= 60) return 'Sage';
    return 'Legend';
}

interface UserProgressContextType {
    xp: number;
    level: number;
    levelTitle: LevelTier;
    streak: number;
    achievements: string[];
    xpProgress: number;
    xpToNextLevel: number;
    isLoading: boolean;
    hasVerifiedProgress: boolean;
    totalMinutes: number;
    longestStreak: number;
}

const UserProgressContext = createContext<UserProgressContextType | undefined>(undefined);

export const UserProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const progressQuery = useUserProgress();
    const progress = user ? progressQuery.data?.progress : undefined;
    const xp = progress?.total_xp ?? 0;
    const level = progress?.level ?? 1;
    const streak = progress?.current_streak ?? 0;
    const longestStreak = progress?.longest_streak ?? 0;
    const totalMinutes = progress?.total_study_minutes ?? 0;
    const isLoading = !user || progressQuery.isPending;
    const hasVerifiedProgress = !!user && !!progress && !progressQuery.isError && !progressQuery.isFetching;
    const [achievementState, setAchievementState] = useState<{ userId: string; names: string[] } | null>(null);
    const achievements = achievementState?.userId === user?.id ? achievementState?.names ?? [] : [];

    useEffect(() => {
        if (!user?.id || !progress?.user_id) return;
        const queryKey = ['user-progress', user.id];
        // Realtime is a refresh signal, not a second source of displayed totals.
        const channel = supabase.channel(`user_progress_${progress.user_id}`)
            .on('postgres_changes', {
                event: '*', schema: 'public', table: 'user_progress', filter: `user_id=eq.${progress.user_id}`,
            }, () => { void queryClient.invalidateQueries({ queryKey }); })
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') void queryClient.invalidateQueries({ queryKey });
            });
        return () => { void supabase.removeChannel(channel); };
    }, [user?.id, progress?.user_id, queryClient]);

    useEffect(() => {
        if (!user?.id) return;
        const userId = user.id;
        let mounted = true;
        const refreshAchievements = async () => {
            const { data, error } = await supabase.from('user_achievements')
                .select('achievements!achievement_id(name)').eq('user_id', userId);
            if (!error && data && mounted) {
                const names = data.map((a: any) => a.achievements?.name).filter((name): name is string => typeof name === 'string');
                setAchievementState({ userId, names });
            }
        };
        void refreshAchievements();
        const channel = supabase.channel(`achievements_${userId}`)
            .on('postgres_changes', {
                event: '*', schema: 'public', table: 'user_achievements', filter: `user_id=eq.${userId}`,
            }, () => { void refreshAchievements(); })
            .subscribe();
        return () => { mounted = false; void supabase.removeChannel(channel); };
    }, [user?.id]);

    // Progress bar math stays on the (now server-matching) tiered curve, but the
    // level number, tier and level-up overlay key off the server's persisted
    // `level` — the canonical value the backend recomputes on every XP change and
    // that level-milestone achievements check against.
    const levelInfo = calculateLevelInfo(xp);
    const serverTier = tierForLevel(level);

    return (
        <UserProgressContext.Provider value={{
            xp,
            level,
            levelTitle: serverTier,
            streak,
            achievements,
            xpProgress: levelInfo.progressPercent,
            xpToNextLevel: levelInfo.xpToNextLevel,
            isLoading,
            hasVerifiedProgress,
            totalMinutes,
            longestStreak
        }}>            {children}
            {/* This provider sits above RouterProvider (see App.tsx), so
                RouteErrorBoundary — which needs router hooks — can't wrap
                these. A bug in either celebration overlay must never take
                the whole app down with it, so each gets its own isolated,
                router-independent boundary that just renders nothing on
                failure instead of a full-page crash screen. */}
            <SilentErrorBoundary name="LevelUpOverlay">
                <LevelUpOverlay key={user?.id ?? "signed-out"} userId={user?.id} level={level} tier={serverTier} isLoading={isLoading} />
            </SilentErrorBoundary>
            <SilentErrorBoundary name="StreakCelebrationOverlay">
                <StreakCelebrationOverlay
                    key={user?.id ?? "signed-out"}
                    userId={user?.id}
                    streak={streak}
                    longestStreak={longestStreak}
                    isLoading={isLoading}
                    hasVerifiedProgress={hasVerifiedProgress}
                />
            </SilentErrorBoundary>
        </UserProgressContext.Provider>
    );
};

export const useUserProgressContext = () => {
    const context = useContext(UserProgressContext);
    if (context === undefined) {
        throw new Error('useUserProgressContext must be used within a UserProgressProvider');
    }
    return context;
};
